import clsx from 'clsx';
import { X } from 'lucide-react';
import { ButtonHTMLAttributes, forwardRef, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { useEscapeLayer } from '../lib/escape';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';

export function Button({
  variant = 'secondary',
  size = 'md',
  className,
  loading,
  children,
  disabled,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'md'; loading?: boolean }) {
  return (
    <button
      {...props}
      disabled={disabled || loading}
      className={clsx(
        'inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        size === 'sm' ? 'h-7 px-2.5 text-xs' : 'h-9 px-3.5 text-sm',
        variant === 'primary' && 'bg-accent text-accent-fg hover:brightness-110',
        variant === 'secondary' && 'border border-border bg-panel hover:bg-muted',
        variant === 'ghost' && 'hover:bg-muted',
        variant === 'danger' && 'bg-danger text-white hover:brightness-110',
        // Verde (ex.: atender ligação): no tema escuro o verde é claro, então o texto fica escuro.
        variant === 'success' && 'bg-ok text-white hover:brightness-110 dark:text-black',
        className,
      )}
    >
      {loading && <Spinner className="size-3.5" />}
      {children}
    </button>
  );
}

const field = 'w-full rounded-md border border-border bg-panel px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(({ className, ...props }, ref) => (
  <input ref={ref} {...props} className={clsx(field, 'h-9 py-0', className)} />
));

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(({ className, ...props }, ref) => (
  <textarea ref={ref} {...props} className={clsx(field, 'resize-none', className)} />
));

export function Select({ className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...props} className={clsx(field, 'h-9 py-0', className)}>
      {children}
    </select>
  );
}

export function Label({ children, hint }: { children: ReactNode; hint?: ReactNode }) {
  return (
    <label className="mb-1 block text-xs font-medium text-fg-soft">
      {children}
      {hint && <span className="ml-1 font-normal">{hint}</span>}
    </label>
  );
}

export function Field({ label, hint, children }: { label: ReactNode; hint?: ReactNode; children: ReactNode }) {
  return (
    <div className="mb-3">
      <Label hint={hint}>{label}</Label>
      {children}
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <span className={clsx('inline-block animate-spin rounded-full border-2 border-current border-t-transparent', className ?? 'size-4')} />;
}

export function Badge({ children, tone = 'neutral', className }: { children: ReactNode; tone?: 'neutral' | 'accent' | 'warn' | 'danger' | 'ok'; className?: string }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium leading-none',
        tone === 'neutral' && 'bg-muted text-fg-soft',
        tone === 'accent' && 'bg-accent/15 text-accent',
        tone === 'warn' && 'bg-warn/15 text-warn',
        tone === 'danger' && 'bg-danger/15 text-danger',
        tone === 'ok' && 'bg-ok/15 text-ok',
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  width = 'max-w-lg',
}: {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
}) {
  useEscapeLayer(open, onClose);
  if (!open) return null;
  return (
    // Celular: tela inteira (título em cima, conteúdo rolando, botões fixos embaixo).
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 md:p-4 md:pt-[8vh]" onMouseDown={onClose}>
      <div
        className={clsx('flex w-full flex-col bg-panel shadow-xl max-md:h-full max-md:max-w-none md:rounded-lg md:border md:border-border', width)}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal
      >
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-4 py-3 md:px-5">
          <h2 className="min-w-0 text-base font-semibold">{title}</h2>
          <button className="rounded p-1.5 text-fg-soft hover:bg-muted" onClick={onClose} aria-label="Fechar">
            <X className="size-5 md:size-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 md:max-h-[70vh] md:px-5">{children}</div>
        {footer && <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-border px-4 py-3 md:px-5">{footer}</div>}
      </div>
    </div>
  );
}

export function ErrorText({ error }: { error: unknown }) {
  if (!error) return null;
  return <p className="mt-2 rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">{error instanceof Error ? error.message : String(error)}</p>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="flex h-full items-center justify-center p-6 text-center text-sm text-fg-soft">{children}</div>;
}
